import React from 'react'
import { useSelector } from 'react-redux'
import { TAG_MULTIPATH_CTA } from '../analytics/analytics.tagids.constants'
import {
  selectCurrentModeError,
  selectCurrentProvider,
  selectIsComputingDone,
  selectIsMonomode
} from '../../store/itinerary/itinerary.selectors'
import {
  selectCurrentRouteId,
  selectDoesCurrentModeHaveRoutes,
  selectDoesCurrentProviderHaveRoutes,
  selectRoutesForMonomode,
  selectRoutesForMultimode
} from '../../store/itinerary/routes.selectors'
import { PUB_TAG } from '../analytics/PublisherTagsService'
import TagActionButton from '../genericComponents/TagActionButton'
import TextError from '../genericComponents/TextError'
import ItineraryTextError from './ItineraryTextError'
import ProviderFooter from './ProviderFooter'
import Route from './Route'
import RouteHighlighter from './RouteHighlighter'
import s from './RouteList.less'
import RouteListPlaceholder from './RouteListPlaceholder'
import FakeTrainRoute from './specialcases/FakeTrainRoute'
import TcRedirectionRoute from './specialcases/TcRedirectionRoute'
import useItineraryRouteError from './useItineraryRouteError'
import CarpoolIncentive from './specialcases/CarpoolIncentive'

const RouteListForMonomodeItem = ({ route, providerIndex, selected, selectedRouteDomRef }) => {
  return (
    <TagActionButton
      tagInfo={{
        provider: route.provider,
        tag: providerIndex === 0 ? PUB_TAG.PUB_MULTIPATH_RESULTAT_TOP : PUB_TAG.PUB_MULTIPATH_RESULTAT
      }}
      type={TAG_MULTIPATH_CTA}
      key={route.routeId}
    >
      <Route route={route} selected={selected} key={route.routeId} selectedRouteDomRef={selectedRouteDomRef} />
    </TagActionButton>
  )
}

const RouteListMonomode = () => {
  const selectedRouteDomRef = React.useRef()

  const currentRouteId = useSelector(selectCurrentRouteId)
  const isComputingDone = useSelector(selectIsComputingDone)
  const routesByProvider = useSelector(selectRoutesForMonomode)
  const currentProviderHaveRoutes = useSelector(selectDoesCurrentProviderHaveRoutes)
  const currentModeHaveRoutes = useSelector(selectDoesCurrentModeHaveRoutes)
  const currentModeError = useSelector(selectCurrentModeError)
  const currentProvider = useSelector(selectCurrentProvider)
  const isMonomode = useSelector(selectIsMonomode)

  const providers = Object.keys(routesByProvider)

  const error = useItineraryRouteError(currentModeError)

  return (
    <>
      {isComputingDone && !currentModeHaveRoutes && <ItineraryTextError error={error} />}
      {!isComputingDone && !currentModeHaveRoutes && <RouteListPlaceholder />}
      {providers.map((provider, idx) => {
        const providerInfo = routesByProvider[provider][0].info
        const nextProviderInfo = routesByProvider[providers[idx + 1]]?.[0].info

        return (
          <div key={idx}>
            <ul className={s.list} key={provider}>
              {provider === currentProvider && currentProviderHaveRoutes && (
                <RouteHighlighter selectedRouteDomRef={selectedRouteDomRef} notify={currentRouteId} />
              )}
              {routesByProvider[provider].map(route => (
                <RouteListForMonomodeItem
                  route={route}
                  selected={route.routeId === currentRouteId}
                  selectedRouteDomRef={selectedRouteDomRef}
                  key={route.routeId}
                  providerIndex={idx}
                />
              ))}
              {isMonomode && (
                <ProviderFooter
                  route={routesByProvider[provider][0]}
                  key={idx}
                  hideInfo={providerInfo === nextProviderInfo}
                />
              )}
            </ul>
          </div>
        )
      })}
      <FakeTrainRoute />
      <CarpoolIncentive />
      {isComputingDone && !currentModeHaveRoutes && <TcRedirectionRoute />}
    </>
  )
}

const RouteListForMultimodeItem = ({ route, providerIndex, selected, selectedRouteDomRef }) => {
  return (
    <TagActionButton
      tagInfo={{
        provider: route.provider,
        tag: providerIndex === 0 ? PUB_TAG.PUB_MULTIPATH_RESULTAT_TOP : PUB_TAG.PUB_MULTIPATH_RESULTAT
      }}
      type={TAG_MULTIPATH_CTA}
      key={route.routeId}
    >
      <Route route={route} selected={selected} key={route.routeId} selectedRouteDomRef={selectedRouteDomRef} />
    </TagActionButton>
  )
}

const RouteListMultimode = () => {
  const selectedRouteDomRef = React.useRef()

  const currentRouteId = useSelector(selectCurrentRouteId)
  const isComputingDone = useSelector(selectIsComputingDone)
  const routes = useSelector(selectRoutesForMultimode)
  const currentModeHaveRoutes = useSelector(selectDoesCurrentModeHaveRoutes)
  const currentModeError = useSelector(selectCurrentModeError)
  const error = useItineraryRouteError(currentModeError)

  return (
    <>
      {isComputingDone && !currentModeHaveRoutes && <TextError error={error} />}
      {!isComputingDone && !currentModeHaveRoutes && <RouteListPlaceholder />}
      <ul className={s.list}>
        <RouteHighlighter selectedRouteDomRef={selectedRouteDomRef} notify={`${routes.length}-${currentRouteId}`} />
        {routes.map(route => (
          <RouteListForMultimodeItem
            route={route}
            selected={route.routeId === currentRouteId}
            selectedRouteDomRef={selectedRouteDomRef}
            key={route.routeId}
          />
        ))}
      </ul>
    </>
  )
}

const RouteList = () => (useSelector(selectIsMonomode) ? <RouteListMonomode /> : <RouteListMultimode />)

export default RouteList
